// src/pages/AssignLead.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Heading,
  Select,
  VStack,
  HStack,
  Text,
  useToast,
  Spinner,
  Flex,
  FormLabel,
} from '@chakra-ui/react';
import {
  collection,
  onSnapshot,
  doc,
  getDoc,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebaseConfig';
import useOrgSettings from '../hooks/useOrgSettings';

export default function AssignLead() {
  const toast = useToast();
  const navigate = useNavigate();
  const { orgId } = useOrgSettings();

  // ✅ Route param fix: the route is /leads/:id/assign
  const { id } = useParams();
  const leadId = id;

  const functions = useMemo(() => getFunctions(undefined, 'europe-west2'), []);
  const fnAssign = useMemo(() => httpsCallable(functions, 'adminAssignLead'), [functions]);

  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState(null);
  const [salespeople, setSalespeople] = useState([]);
  const [salespersonId, setSalespersonId] = useState('');

  useEffect(() => {
    let unsub = () => {};
    (async () => {
      try {
        if (!orgId || !leadId) return;
        setLoading(true);

        // Load the lead
        const d = await getDoc(doc(db, `organizations/${orgId}/leads/${leadId}`));
        if (d.exists()) {
          const ld = { id: d.id, ...d.data() };
          setLead(ld);
          setSalespersonId(ld.salespersonId || '');
        } else {
          setLead(null);
          toast({ title: 'Lead not found', status: 'error', isClosable: true });
        }

        // Live salespeople list, excluding archived
        unsub = onSnapshot(
          collection(db, `organizations/${orgId}/salespeople`),
          (snap) => {
            const all = snap.docs.map((x) => ({ id: x.id, ...x.data() }));
            const active = all
              .filter((sp) => (sp.status || 'active').toLowerCase() !== 'archived') // ✅ exclude archived
              .sort((a, b) => {
                const an = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
                const bn = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
                return an.localeCompare(bn);
              });
            setSalespeople(active);
          },
          (err) => {
            console.error('[AssignLead] salespeople listen error:', err);
            toast({ title: 'Error loading salespeople', status: 'error', isClosable: true });
          }
        );
      } finally {
        setLoading(false);
      }
    })();
    return () => unsub();
  }, [orgId, leadId, toast]);

  const handleAssign = async () => {
    if (!salespersonId) {
      toast({ title: 'Pick a sales rep', status: 'info', isClosable: true });
      return;
    }
    try {
      const sp = salespeople.find((s) => s.id === salespersonId);
      await fnAssign({
        orgId,
        leadId,
        salespersonId,
        salespersonName:
          `${sp?.firstName || ''} ${sp?.lastName || ''}`.trim() ||
          sp?.displayName ||
          sp?.email ||
          'Sales Rep',
      });
      toast({ title: 'Lead assigned', status: 'success', isClosable: true });
      navigate('/leads');
    } catch (e) {
      console.error(e);
      toast({ title: 'Assign failed', description: e.message, status: 'error', isClosable: true });
    }
  };

  if (loading) {
    return (
      <HStack spacing={4} p={6}>
        <Spinner size="lg" />
        <Text fontSize="lg">Loading…</Text>
      </HStack>
    );
  }

  return (
    <Box borderWidth={1} borderRadius="lg" p={6} boxShadow="lg" bg="white">
      <Heading size="lg" mb={6} color="blue.600">
        Assign Lead
      </Heading>
      <Text fontSize="md" color="gray.600" mb={6}>
        Assign a sales representative to this lead.
      </Text>

      <VStack align="stretch" spacing={6}>
        <Box>
          <Text fontWeight="semibold">
            {lead?.customerFullName ||
              `${lead?.customerFirstName || ''} ${lead?.customerLastName || ''}`.trim() ||
              '—'}
          </Text>
          <Text color="gray.600" fontSize="sm">
            {lead?.postcode || '—'}
          </Text>
        </Box>

        <Box>
          <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
            Sales Rep
          </FormLabel>
          <Select
            value={salespersonId}
            onChange={(e) => setSalespersonId(e.target.value)}
            size="lg"
            maxW="320px"
          >
            <option value="">— Select —</option>
            {salespeople.length === 0 ? (
              <option value="" disabled>
                (No active salespeople)
              </option>
            ) : (
              salespeople.map((sp) => (
                <option key={sp.id} value={sp.id}>
                  {sp.displayName ||
                    `${sp.firstName || ''} ${sp.lastName || ''}`.trim() ||
                    sp.email}
                </option>
              ))
            )}
          </Select>
        </Box>

        <Flex justify="flex-end" pt={4}>
          <HStack spacing={4}>
            <Button variant="ghost" onClick={() => navigate('/leads')}>
              Cancel
            </Button>
            <Button onClick={handleAssign} colorScheme="blue" size="lg">
              Assign
            </Button>
          </HStack>
        </Flex>
      </VStack>
    </Box>
  );
}
